/**
 * An automation read at a glance, as the redesign's Automations card draws it (c-build/flow.js `flLine`): the
 * tag that starts it is the first stop, then each step is a stop on one thin line with a short title and one
 * fact. A branch forks honestly: the line carries on along the path that has steps and names the other ("Yes:
 * ends"), or splits in two when both have steps. Faces sit on the stop where people are waiting, with a soft
 * pulse. Compact draws icons only, with a count, for Home and a room's list.
 */
import { Fragment, type ReactNode } from "react";
import type { AutomationSummary, StopWhen, TriggerJson } from "../../shared/types";
import { startsEachSession } from "../../shared/tags";
import { splitAddresses } from "../../shared/addresses";
import { graphToTree, type Step } from "../automations/flow-model";
import { AskWords, kindClass, kindIcon, TagChip, waitWords } from "./FlowEditor";
import { FaceStack } from "./ui/Face";
import { Icon, type IconName } from "./ui/Icon";
import "../styles/flow.css";

/** Who waits at a step right now. */
interface LineHere {
  count: number;
  people: ReadonlyArray<{ name: string; photo?: string | null }>;
}

export interface FlowLineProps {
  trigger: TriggerJson;
  stopWhen: StopWhen;
  /** The steps as the builder holds them (`graphToTree`). */
  steps: Step[];
  /** People waiting, by step id; "start" for runs not begun. */
  here?: Readonly<Record<string, LineHere>>;
  /** One fact per step, by step id: "1,540 sent", "708 tagged". */
  facts?: Readonly<Record<string, string>>;
  /** Icons only, with a count where people wait. */
  compact?: boolean;
  /** True when the automation runs on a room that repeats, so "Each session" means something. */
  repeats?: boolean;
}

const fmt = (n: number) => n.toLocaleString("en-US");

/** The line's props from an automation as the list gives it. */
export function summaryLine(a: Pick<AutomationSummary, "trigger" | "stop_when" | "graph" | "line" | "repeats">): FlowLineProps {
  return { trigger: a.trigger, stopWhen: a.stop_when, steps: graphToTree(a.graph), here: a.line.here, facts: a.line.facts, repeats: a.repeats };
}

/** The person a team alert goes to, by the first part of the first address: "Notify Maya". */
function notifyWho(addresses: string): string {
  const first = splitAddresses(addresses)[0] ?? "";
  const word = first.split("@")[0]?.split(/[._+-]/)[0] ?? "";
  return word ? `Notify ${word.charAt(0).toUpperCase()}${word.slice(1)}` : "Notify a person";
}

/** A step's short title on the line, and the same as plain words for a tooltip. */
function shortTitle(step: Step): { node: ReactNode; text: string } {
  switch (step.type) {
    case "email": {
      const t = step.subject.trim() || "No subject yet";
      return { node: t, text: t };
    }
    case "room": {
      const t = step.title.trim() || "Add to their page";
      return { node: t, text: t };
    }
    case "fact": {
      const verb = step.op === "remove" ? "Remove" : "Add";
      return {
        node: (
          <>
            {verb} <TagChip name={step.name} />
          </>
        ),
        text: `${verb} ${step.name}`,
      };
    }
    case "webhook":
      return { node: "Send to a webhook", text: "Send to a webhook" };
    case "notify": {
      const t = notifyWho(step.email);
      return { node: t, text: t };
    }
    case "wait": {
      const t = waitWords(step, true);
      return { node: t, text: t };
    }
    case "branch": {
      const a = step.ask;
      if (a.kind === "tag" || a.kind === "notag") {
        const lead = a.kind === "tag" ? "Has" : "Doesn’t have";
        return {
          node: (
            <>
              {lead}{" "}
              <span className="al-q">
                <TagChip name={a.tag ?? ""} />?
              </span>
            </>
          ),
          text: `${lead} ${a.tag ?? ""}?`,
        };
      }
      return { node: <AskWords ask={a} />, text: "A question" };
    }
  }
}

function Stop({
  kind,
  icon,
  title,
  text,
  fact,
  here,
  lab,
  compact,
}: {
  kind: string;
  icon: IconName;
  title: ReactNode;
  text: string;
  fact: string;
  here?: LineHere;
  lab?: string;
  compact: boolean;
}) {
  const waiting = here && here.count > 0 ? here : null;
  return (
    <div className={`al-s k-${kind}`} {...(compact ? { title: `${text}${waiting ? ` · ${fmt(waiting.count)} waiting` : ""}` } : {})}>
      {lab ? <span className="al-lab">{lab}</span> : null}
      <span className={waiting ? "al-n here" : "al-n"}>
        <Icon name={icon} />
        {waiting && compact ? <span className="al-badge">{fmt(waiting.count)}</span> : null}
      </span>
      {waiting && !compact ? (
        <span className="al-here">
          <FaceStack people={waiting.people.slice(0, 3)} size={22} />
        </span>
      ) : null}
      {compact ? null : (
        <div className="al-x">
          <div className="al-t">{title}</div>
          {fact ? <div className="al-f">{fact}</div> : null}
        </div>
      )}
    </div>
  );
}

export function FlowLine({ trigger, stopWhen, steps, here = {}, facts = {}, compact = false, repeats = false }: FlowLineProps) {
  const end = <span className="al-end" aria-hidden="true" />;

  const factFor = (step: Step): string => {
    const waiting = here[step.uid];
    if (waiting && waiting.count > 0) return `${fmt(waiting.count)} waiting`;
    return facts[step.uid] ?? "";
  };

  const seq = (list: Step[], key: string): ReactNode[] => {
    const out: ReactNode[] = [];
    for (const step of list) {
      const t = shortTitle(step);
      const common = { kind: kindClass(step.type), icon: kindIcon(step.type), title: t.node, text: t.text, compact, here: here[step.uid] };
      if (step.type !== "branch") {
        out.push(<Stop key={step.uid} {...common} fact={factFor(step)} />);
        continue;
      }
      const { yes, no } = step;
      if (yes.length && no.length) {
        out.push(
          <Stop key={step.uid} {...common} fact={facts[step.uid] ?? ""} />,
          <div className="al-fork" key={`${step.uid}-fork`}>
            <div className="al-path">
              <span className="al-pl">Yes</span>
              {seq(yes, `${key}-y`)}
            </div>
            <div className="al-path">
              <span className="al-pl">No</span>
              {seq(no, `${key}-n`)}
            </div>
          </div>,
        );
        return out;
      }
      const goOn = no.length ? no : yes;
      if (!goOn.length) {
        out.push(<Stop key={step.uid} {...common} fact="Both end" />, <Fragment key={`${step.uid}-end`}>{end}</Fragment>);
        return out;
      }
      out.push(<Stop key={step.uid} {...common} fact={`${no.length ? "Yes" : "No"}: ends`} lab={no.length ? "No" : "Yes"} />, ...seq(goOn, key));
      return out;
    }
    out.push(<Fragment key={`${key}-end`}>{end}</Fragment>);
    return out;
  };

  const stopTags = stopWhen ? [stopWhen.tag, ...(stopWhen.more ?? [])].filter(Boolean) : [];
  const whenFact = stopTags.length
    ? `Stops ${stopWhen?.signal === "not_has_tag" ? "without" : "on"} ${stopTags.join(" or ")}`
    : repeats && startsEachSession(trigger)
      ? "Each session"
      : "";
  const verb = trigger.type === "tag_removed" ? "Loses" : "Gets";

  return (
    <div className={compact ? "al compact" : "al"}>
      <Stop
        kind="when"
        icon="tag"
        title={
          <>
            {verb} <TagChip name={trigger.value} />
          </>
        }
        text={`${verb} ${trigger.value}`}
        fact={whenFact}
        here={here.start}
        compact={compact}
      />
      {steps.length ? seq(steps, "main") : end}
    </div>
  );
}
